import { ButtonHTMLAttributes, MouseEventHandler, ReactNode } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { BRAND_COLORS, hexToRgba } from "@tami/tokens";

export type TamiButtonVariant = "black" | "primary" | "outline" | "glass" | "light" | "pink";
export type TamiButtonSize = "default" | "lg";

interface TamiButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  onClick?: MouseEventHandler<HTMLElement>;
  /**
   * Variante do botão
   * - black: Fundo preto com texto branco
   * - primary: Fundo verde Tami com texto preto
   * - outline: Borda preta com fundo off-white e ícone
   * - glass: Efeito glassmorphism com fundo escuro translúcido
   * - light: Fundo cor Tami Light (#FBF5DF) com texto preto
   * - pink: Fundo rosa Tami com texto escuro (branco no rosa dá 3,2:1 e reprova
   *   o contraste mínimo de 4,5:1 do WCAG AA para texto de 14-16px)
   */
  variant?: TamiButtonVariant;
  /**
   * Tamanho do botão
   */
  size?: TamiButtonSize;
  /**
   * Se fornecido, renderiza como Link do Next.js
   */
  href?: string;
  /**
   * Ícone opcional (lado esquerdo)
   */
  icon?: ReactNode;
  /**
   * Conteúdo do botão
   */
  children: ReactNode;
  /**
   * Classes CSS adicionais
   */
  className?: string;
}

/**
 * `black`, `primary` e `outline` pintam com variáveis CSS (`--tami-green`,
 * `--tami-dark-gray`, `--tami-black`, `--tami-off-white`) definidas só em
 * `apps/web/src/app/[lang]/(home)/styles/home.style.css`, importada só pela home —
 * de propósito: a home inverte esse trio de cores por tema, e as três
 * variantes acompanham essa inversão. Fora da home essas variáveis não
 * existem: o fundo fica transparente e o texto herda a cor do texto ao
 * redor (foi assim que a `/links` saiu com o botão sem fundo). Quem usa
 * `TamiButton` fora da home precisa sobrescrever fundo e texto por
 * `className`, com classe de tema (`bg-tami-*`/`text-tami-*`), nunca cor
 * literal — veja `bio-collab-block.tsx` para um exemplo desse override.
 */
const variantStyles: Record<TamiButtonVariant, string> = {
  black: cn(
    "bg-[var(--tami-black)] text-[var(--tami-off-white)]",
    "hover:bg-tami-dark-hover",
    "border-none"
  ),
  primary: cn(
    "bg-[var(--tami-green)] text-[var(--tami-dark-gray)]",
    "hover:bg-tami-green-hover",
    "border border-black"
  ),
  outline: cn(
    "bg-[var(--tami-off-white)] text-[var(--tami-dark-gray)]",
    "border-2 border-[var(--tami-dark-gray)]",
    "hover:bg-tami-light-hover"
  ),
  glass: cn(
    "relative bg-gradient-to-br from-white/10 via-white/5 to-white/10",
    "border border-white/10 text-white",
    "shadow-lg overflow-hidden",
    "cursor-pointer",
    "hover:bg-white/20 hover:scale-105 transition-all duration-300",
    "active:scale-100"
  ),
  light: cn(
    "bg-tami-light text-black border border-black/10",
    "hover:bg-tami-light-hover-soft",
  ),
  pink: cn(
    "bg-tami-pink text-tami-dark border-none",
    "hover:bg-tami-pink-hover",
  ),
};

const sizeStyles: Record<TamiButtonSize, string> = {
  default: "px-8 py-3 text-base",
  lg: "px-10 py-4 text-lg",
};

/**
 * Botão Tami com 3 variantes baseadas no design system
 * 
 * @example
 * ```tsx
 * // Botão preto
 * <TamiButton variant="black">acessar</TamiButton>
 * 
 * // Botão primário (verde)
 * <TamiButton variant="primary" href="/sign">Crie sua conta</TamiButton>
 * 
 * // Botão com borda e ícone
 * <TamiButton variant="outline" icon={<IconComponent />}>
 *   Crie sua conta
 * </TamiButton>
 * ```
 */
export const TamiButton = ({
  variant = "primary",
  size = "default",
  href,
  icon,
  children,
  className,
  onClick,
  ...props
}: TamiButtonProps) => {
  const baseStyles = cn(
    "inline-flex items-center justify-center gap-2",
    "rounded-[100px]", // Borda arredondada (pill shape)
    "font-semibold",
    variant !== "glass" && "transition-all duration-200",
    "focus:outline-none focus:ring-2 focus:ring-offset-2",
    variant !== "glass" && "focus:ring-[var(--tami-dark-gray)]",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "whitespace-nowrap",
    variant === "glass" && "transform-gpu"
  );

  const buttonClasses = cn(
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    className
  );

  const content = (
    <>
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span>{children}</span>
    </>
  );

  // Render glass effect
  if (variant === "glass") {
    const glassContent = (
      <>
        {/* Brilho principal */}
        <div 
          className="absolute inset-0 rounded-full" 
          style={{
            background: `linear-gradient(135deg, ${hexToRgba(BRAND_COLORS.white, 0.15)} 0%, transparent 60%)`,
            boxShadow: `inset 0 1px 0 ${hexToRgba(BRAND_COLORS.white, 0.3)}`
          }}
        />
        {content}
      </>
    );

    if (href) {
      return (
        <Link
          href={href}
          className={buttonClasses}
          onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        >
          {glassContent}
        </Link>
      );
    }

    return (
      <button className={buttonClasses} onClick={onClick} {...props}>
        {glassContent}
      </button>
    );
  }

  // Normal render
  if (href) {
    return (
      <Link
        href={href}
        className={buttonClasses}
        onClick={onClick as MouseEventHandler<HTMLAnchorElement>}
        // Sem o spread, toda prop extra (`data-testid`, `aria-*`) some sem
        // aviso neste ramo e sobrevive no de botão — o componente parecia
        // aceitá-las e silenciosamente não as aplicava.
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </Link>
    );
  }

  return (
    <button className={buttonClasses} onClick={onClick} {...props}>
      {content}
    </button>
  );
};

/**
 * Variantes específicas para uso rápido
 */
export const TamiButtonBlack = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="black" {...props} />
);

export const TamiButtonPrimary = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="primary" {...props} />
);

export const TamiButtonOutline = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="outline" {...props} />
);

export const TamiButtonGlass = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="glass" {...props} />
);

export const TamiButtonLight = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="light" {...props} />
);

export const TamiButtonPink = (props: Omit<TamiButtonProps, "variant">) => (
  <TamiButton variant="pink" {...props} />
);

